/**
 * Registry Item Gateway (RIG) API Service
 *
 * Provides access to RIG endpoints for registry-related data
 * Ported from FE-Member-Registry: src/services/RegistryItemGatewayApi.js
 */

import axios from 'axios';
import { get } from 'js-cookie';

// Gift Advisor Summary response structure (from FEMR)
export interface GiftBucket {
  added: number;
  total: number;
  shopLinkParam: string;
  simpleLabel: string;
  label: string;
}

export interface GiftAdvisorSummaryResponse {
  totalNonCashGiftsRegistered?: number;
  giftBuckets: GiftBucket[];
}

/**
 * Get the RIG API host based on environment
 * Uses REACT_APP_RIG_API_ENDPOINT if available, otherwise falls back to:
 * QA: https://qa-registry-item-gateway.regsvcs.theknot.com
 * Production: https://registry-item-gateway.regsvcs.theknot.com
 */
const getRigApiHost = (): string => {
  // Use environment variable if provided
  if (process.env.REACT_APP_RIG_API_ENDPOINT) {
    return process.env.REACT_APP_RIG_API_ENDPOINT;
  }

  const env = process.env.REACT_APP_APP_ENV || 'qa';

  if (env === 'production') {
    return 'https://registry-item-gateway.regsvcs.theknot.com';
  }

  return 'https://qa-registry-item-gateway.regsvcs.theknot.com';
};

/**
 * Get session token from cookies
 */
const getSessionToken = (): string => {
  return get('xo-session-token') || '';
};

/**
 * Base headers for RIG API calls
 * Following FEMR pattern with membership-session-token
 */
const baseHeaders = () => ({
  'membership-session-token': getSessionToken(),
  Accept: 'application/json',
  'Content-Type': 'application/json',
});

class RegistryItemGatewayApiService {
  /**
   * Get Gift Advisor Summary
   *
   * Fetches gift recommendation statistics grouped by price range
   *
   * FEMR Reference:
   * - services/RegistryItemGatewayApi.js::getGiftAdvisorSummary
   * - contexts/GiftAdvisorSummaryContext.js::fetchGiftAdvisorSummary
   *
   * @param numberOfGuests - Guest count range for calculating recommendations (e.g., "101–150") (optional)
   * @param registryId - Optional registry ID (defaults to member's registry)
   * @returns Promise with giftBuckets array
   */
  static async getGiftAdvisorSummary({
    numberOfGuests,
    registryId,
  }: {
    numberOfGuests?: string;
    registryId?: string;
  } = {}): Promise<GiftAdvisorSummaryResponse> {
    const params = new URLSearchParams();

    if (registryId) {
      params.append('registryId', registryId);
    }

    if (numberOfGuests !== undefined) {
      params.append('numberOfGuests', numberOfGuests);
    }

    const url = `${getRigApiHost()}/gift-advisor-summary${params.toString() ? `?${params.toString()}` : ''}`;

    const response = await axios.get<GiftAdvisorSummaryResponse>(url, {
      headers: baseHeaders(),
    });

    return response.data;
  }
}

export default RegistryItemGatewayApiService;
